import { useEffect, useRef, useState } from 'react';

import {
  useLazyGetValueChainMapBoardGroupsQuery,
  useLazyGetValueChainMapBoardUnitsQuery,
} from '../../../../../../store/slices/valueChainMap/apis/valueChainMapApi';

import type { IBoardInstanceGroup } from '../../../../../../store/slices/valueChainMap/interfaces/IBoardInstanceGroup';
import type { IBoardUnit } from '../../../../../../store/slices/valueChainMap/interfaces/IBoardUnit';
import type {
  IUseValueChainBoardGroupsParams,
  IUseValueChainBoardGroupsReturn,
} from '../interfaces/IUseValueChainBoardGroups';

const normalizeList = <TItem>(value: unknown): TItem[] => {
  if (Array.isArray(value)) {
    return value as TItem[];
  }

  const wrapped = (value as { data?: TItem[] } | undefined)?.data;
  return Array.isArray(wrapped) ? wrapped : [];
};

const useValueChainBoardGroups = ({
  valueChainMapId,
  boards,
  type,
  enabled,
  filters,
  followConnections,
}: IUseValueChainBoardGroupsParams): IUseValueChainBoardGroupsReturn => {
  const [triggerGroups] = useLazyGetValueChainMapBoardGroupsQuery();
  const [triggerUnits] = useLazyGetValueChainMapBoardUnitsQuery();

  const [groupsByBoardId, setGroupsByBoardId] = useState<Record<number, IBoardInstanceGroup[]>>({});
  const [unitsByBoardId, setUnitsByBoardId] = useState<Record<number, IBoardUnit[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  // Monotonic id so a superseded in-flight load (e.g. rapid Location / Business
  // Structure / Cards switching) can never overwrite the latest data with stale
  // results, and a load that started before the hook was disabled is ignored.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !valueChainMapId || boards.length === 0) {
      latestRequestIdRef.current += 1;
      setGroupsByBoardId({});
      setUnitsByBoardId({});
      setIsLoading(false);
      return undefined;
    }

    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    setIsLoading(true);
    // Intentionally do NOT clear the current groups/units here: keep the board,
    // cards and connections on screen until the new data resolves so they never
    // blank out (and get stuck empty) while switching grouping modes.

    const loadAll = async () => {
      const results = await Promise.all(boards.map(async (board) => {
        const boardId = String(board.id);
        // No preferCacheValue: always resolve against a real request so a
        // stale/empty cache entry from a previous mode round-trip can't be
        // returned without refetching (which dropped units, hence connections).
        const [groups, units] = await Promise.all([
          triggerGroups({
            valueChainMapId, boardId, type, filters, followConnections,
          })
            .unwrap()
            .catch(() => [] as IBoardInstanceGroup[]),
          triggerUnits({
            valueChainMapId, boardId, type, filters, followConnections,
          })
            .unwrap()
            .catch(() => [] as IBoardUnit[]),
        ]);

        return {
          boardId: board.id,
          groups: normalizeList<IBoardInstanceGroup>(groups),
          units: normalizeList<IBoardUnit>(units),
        };
      }));

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      const nextGroups: Record<number, IBoardInstanceGroup[]> = {};
      const nextUnits: Record<number, IBoardUnit[]> = {};

      results.forEach(({ boardId, groups, units }) => {
        nextGroups[boardId] = groups;
        nextUnits[boardId] = units;
      });

      setGroupsByBoardId(nextGroups);
      setUnitsByBoardId(nextUnits);
      setIsLoading(false);
    };

    loadAll();

    return undefined;
  }, [enabled, valueChainMapId, boards, type, filters, followConnections, triggerGroups, triggerUnits]);

  return { groupsByBoardId, unitsByBoardId, isLoading };
};

export default useValueChainBoardGroups;
